import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import NotificationContainer from '../../../components/Notification/NotificationContainer';
import styles from './Register.module.css';

const Register = () => {
  const navigate = useNavigate();
  const { register, isLoading } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    password_confirmation: '',
    user_type: 'customer'
  });

  const [notifications, setNotifications] = useState([]);
  const [errors, setErrors] = useState({});

  const addNotification = (message, type = 'success', duration = 5000) => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [...prev, { id, message, type, duration }]);
    setTimeout(() => {
      removeNotification(id);
    }, duration);
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Effacer l'erreur pour ce champ
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'L\'email n\'est pas valide';
    }

    if (!formData.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    }

    if (!formData.password_confirmation) {
      newErrors.password_confirmation = 'La confirmation du mot de passe est requise';
    } else if (formData.password !== formData.password_confirmation) {
      newErrors.password_confirmation = 'Les mots de passe ne correspondent pas';
    }

    if (formData.phone && !/^[0-9+\s-]+$/.test(formData.phone)) {
      newErrors.phone = 'Le numéro de téléphone n\'est pas valide';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      addNotification('Veuillez corriger les erreurs dans le formulaire', 'error');
      return;
    }

    const result = await register({
      name: formData.name,
      email: formData.email,
      phone: formData.phone || null,
      password: formData.password,
      password_confirmation: formData.password_confirmation,
      user_type: formData.user_type
    });

    if (result.success) {
      // Vérifier si le compte nécessite une validation
      const requiresApproval = result.requires_approval ||
                               (result.data?.requires_approval) ||
                               (formData.user_type !== 'customer');

      if (requiresApproval) {
        addNotification(
          'Inscription réussie ! Votre compte est en attente de validation par un administrateur. Vous recevrez un email une fois votre compte activé.',
          'info',
          10000 // Durée d'affichage de 10 secondes pour laisser le temps de lire
        );
        // Rediriger vers la page de connexion pour qu'il puisse se connecter après validation
        // Délai plus long pour laisser le temps de lire le message
        setTimeout(() => {
          navigate('/login');
        }, 10000);
      } else {
        addNotification('Inscription réussie ! Bienvenue !', 'success');
        // Rediriger vers la page d'accueil pour les clients actifs
        setTimeout(() => {
          navigate('/');
        }, 1000);
      }
    } else {
      // Gérer les erreurs de validation du serveur
      if (result.error) {
        addNotification(result.error, 'error');
      }
      // Si c'est une erreur de validation avec des champs spécifiques
      if (result.errors) {
        // Convertir les erreurs Laravel (tableau) en format simple
        const formattedErrors = {};
        Object.keys(result.errors).forEach(key => {
          formattedErrors[key] = Array.isArray(result.errors[key])
            ? result.errors[key][0]
            : result.errors[key];
        });
        setErrors(formattedErrors);
      }
    }
  };

  return (
    <div className={styles.body}>
      <NotificationContainer notifications={notifications} removeNotification={removeNotification} />
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>INSCRIPTION</h1>
        </div>
        <div className={styles.formContainer}>
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.inputGroup}>
              <label htmlFor="name" className={styles.srOnly}>
                Nom complet
              </label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Nom complet *"
                value={formData.name}
                onChange={handleInputChange}
                className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
                required
              />
              {errors.name && <span className={styles.errorText}>{errors.name}</span>}
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="email" className={styles.srOnly}>
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Email *"
                value={formData.email}
                onChange={handleInputChange}
                className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                required
              />
              {errors.email && <span className={styles.errorText}>{errors.email}</span>}
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="phone" className={styles.srOnly}>
                Téléphone
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                placeholder="Téléphone (optionnel)"
                value={formData.phone}
                onChange={handleInputChange}
                className={`${styles.input} ${errors.phone ? styles.inputError : ''}`}
              />
              {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="user_type" className={styles.srOnly}>
                Type de compte
              </label>
              <select
                id="user_type"
                name="user_type"
                value={formData.user_type}
                onChange={handleInputChange}
                className={styles.select}
              >
                <option value="customer">👤 Client</option>
                <option value="supplier">🏪 Fournisseur</option>
                <option value="delivery_person">🚚 Livreur</option>
              </select>
              <p className={styles.helpText}>
                {formData.user_type === 'customer' && (
                  <>
                    <span className={styles.infoIcon}>✓</span> En tant que client, vous pouvez immédiatement acheter des produits agricoles. Votre compte sera automatiquement activé.
                  </>
                )}
                {formData.user_type === 'supplier' && (
                  <>
                    <span className={styles.warningIcon}>⏳</span> En tant que fournisseur, votre compte sera en attente de validation par un administrateur. Vous recevrez un email une fois votre compte activé.
                  </>
                )}
                {formData.user_type === 'delivery_person' && (
                  <>
                    <span className={styles.warningIcon}>⏳</span> En tant que livreur, votre compte sera en attente de validation par un administrateur. Vous recevrez un email une fois votre compte activé.
                  </>
                )}
              </p>
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="password" className={styles.srOnly}>
                Mot de passe
              </label>
              <input
                type="password"
                id="password"
                name="password"
                placeholder="Mot de passe * (min. 8 caractères)"
                value={formData.password}
                onChange={handleInputChange}
                className={`${styles.input} ${errors.password ? styles.inputError : ''}`}
                required
                minLength="8"
              />
              {errors.password && <span className={styles.errorText}>{errors.password}</span>}
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="password_confirmation" className={styles.srOnly}>
                Confirmer le mot de passe
              </label>
              <input
                type="password"
                id="password_confirmation"
                name="password_confirmation"
                placeholder="Confirmer le mot de passe *"
                value={formData.password_confirmation}
                onChange={handleInputChange}
                className={`${styles.input} ${errors.password_confirmation ? styles.inputError : ''}`}
                required
                minLength="8"
              />
              {errors.password_confirmation && <span className={styles.errorText}>{errors.password_confirmation}</span>}
            </div>

            <div>
              <button
                type="submit"
                className={styles.submitButton}
                disabled={isLoading}
              >
                {isLoading ? 'INSCRIPTION...' : 'S\'INSCRIRE'}
              </button>
            </div>
          </form>
          <div className={styles.signupSection}>
            <p className={styles.signupText}>Vous avez déjà un compte ?</p>
            <Link to="/login" className={styles.signupLink}>
              SE CONNECTER
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;

